import { useState, useEffect } from 'react';
import { FormData, Question } from '../types';
import { evaluateVisibility } from '../questionnaire/conditionEvaluator';

interface QuestionFieldProps {
  question: Question;
  value: any;
  onChange: (id: string, value: any) => void;
  error?: string;
  formData: FormData;
}

export const QuestionField = ({
  question,
  value,
  onChange,
  error,
  formData
}: QuestionFieldProps) => {
  // Skip rendering if question is not visible
  if (!evaluateVisibility(question.visibleWhen, formData)) {
    return null;
  }

  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [otherValue, setOtherValue] = useState('');

  // Handle radio button changes
  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(question.id, newValue);

    // Reset other field when changing selection
    if (question.id.includes('Functions') || question.id.includes('Activities')) {
      setIsOtherSelected(newValue === 'other');
      if (newValue !== 'other') {
        setOtherValue('');
        onChange(`${question.id}Other`, '');
      }
    }
  };

  // Handle select changes
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(question.id, e.target.value);
  };

  // Handle text input changes
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange(question.id, e.target.value);
  };

  // Handle checkbox changes
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(question.id, e.target.checked);
  };

  // Handle multiple choice changes
  const handleMultiChoiceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const optionValue = e.target.value;
    const isChecked = e.target.checked;

    let newValue: string[] = [];

    if (Array.isArray(value)) {
      newValue = [...value];
    }

    if (isChecked) {
      newValue.push(optionValue);
      if (optionValue === 'other') {
        setIsOtherSelected(true);
      }
    } else {
      newValue = newValue.filter(v => v !== optionValue);
      if (optionValue === 'other') {
        setIsOtherSelected(false);
        setOtherValue('');
        onChange(`${question.id}Other`, '');
      }
    }

    onChange(question.id, newValue);
  };

  // Handle other field changes
  const handleOtherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOtherValue(e.target.value);
    onChange(`${question.id}Other`, e.target.value);
  };

  // Initialize other state for multi-choice questions
  useEffect(() => {
    if (question.type === 'checkbox' && Array.isArray(value) && value.includes('other')) {
      setIsOtherSelected(true);
      setOtherValue(formData[`${question.id}Other` as keyof FormData] as string || '');
    }
  }, [question.id, question.type, value, formData]);

  return (
    <div className="form-group">
      <label className={`form-label ${question.required ? 'required' : ''}`}>
        {question.label}
      </label>

      {question.type === 'text' && (
        <input
          type={question.id === 'businessEmail' ? 'email' : 'text'}
          className="form-control"
          value={value || ''}
          onChange={handleTextChange}
          placeholder={
            question.id === 'companyName' ? 'Enter your company name' :
            question.id === 'businessEmail' ? 'name@company.com' : ''
          }
        />
      )}

      {question.type === 'number' && (
        <input
          type="number"
          className="form-control"
          value={value || ''}
          onChange={handleTextChange}
          placeholder="Enter number"
        />
      )}

      {question.type === 'select' && (
        <select
          className="form-control"
          value={value || ''}
          onChange={handleSelectChange}
        >
          <option value="">Select an option</option>
          {question.options?.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}

      {question.type === 'radio' && (
        <div className="radio-group">
          {question.options?.map(option => (
            <div key={option.value} className="radio-option">
              <label>
                <input
                  type="radio"
                  name={question.id}
                  value={option.value}
                  checked={value === option.value}
                  onChange={handleRadioChange}
                />
                {option.label}
              </label>
            </div>
          ))}
        </div>
      )}

      {question.type === 'textarea' && (
        <textarea
          className="form-control"
          rows={question.id === 'drivingInterest' || question.id === 'currentChallenges' ? 5 : 3}
          value={value || ''}
          onChange={handleTextChange}
          placeholder={
            question.id === 'drivingInterest'
              ? 'Please briefly describe the main business reasons, opportunities or developments behind your current interest in China.'
              : question.id === 'currentChallenges'
                ? 'For example: market access, sales, sourcing, regulations, localization, recruitment, production, partner selection or internal resources.'
                : question.id === 'supportPriorities'
                  ? 'Please describe any specific topics where you would like support or further information.'
                  : ''
          }
        />
      )}

      {question.type === 'checkbox' && (
        <div className="checkbox-group">
          {question.options?.map(option => {
            const isChecked = Array.isArray(value) && value.includes(option.value);
            return (
              <div key={option.value} className="checkbox-option">
                <label>
                  <input
                    type="checkbox"
                    name={question.id}
                    value={option.value}
                    checked={isChecked}
                    onChange={handleMultiChoiceChange}
                  />
                  {option.label}
                </label>
              </div>
            );
          })}
        </div>
      )}

      {error && (
        <div className="form-error" style={{ color: '#d32f2f', fontSize: '0.875rem', marginTop: '0.25rem' }}>
          {error}
        </div>
      )}

      {/* Optional comment fields */}
      {question.id.includes('Comment') && (
        <div className="form-text" style={{ marginTop: '0.5rem', fontStyle: 'italic' }}>
          Optional comment
        </div>
      )}
    </div>
  );
};
